import React, { memo, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { readState } from "@/__main__/app-state.mjs";
import { GAME_BOX_ICONS, GAME_COLORS } from "@/app/constants.mjs";
import { WidgetContainer } from "@/app/LiveGameAcceptMatchmaking.style.jsx";
import { useRoute } from "@/util/router-hooks.mjs";
import useKeypress from "@/util/use-key-press.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const LiveGameAcceptMatchmaking = () => {
  const route = useRoute({ isGlobal: true });
  const {
    volatile: { liveGame = {} },
  } = useSnapshot(readState);
  const { t } = useTranslation();

  const {
    show,
    link,
    image,
    game: gameSymbol,
    text = [],
    titleOverride,
    ctaOverride,
    ctaColor = "var(--green)",
    // timestamp,
    acceptAction,
    declineAction,
    countdownDuration, // in seconds
  } = liveGame;

  useKeypress(["Enter", " "], acceptAction, [acceptAction]);
  useKeypress(["Escape", "Backspace"], declineAction, [declineAction]);

  const BLACKLIST_ROUTES = ["/account", "/settings"];

  if (
    BLACKLIST_ROUTES.some((r) => route.currentPath.startsWith(r)) ||
    !show ||
    link === route.currentPath
  ) {
    return null;
  }

  const GameIcon = GAME_BOX_ICONS[gameSymbol];
  const GameColor = GAME_COLORS[gameSymbol];

  const WidgetTag = acceptAction ? "button" : "a";

  return (
    <header
      className={WidgetContainer()}
      style={{
        "--game-color": GameColor,
        "--cta-color": ctaColor,
      }}
    >
      <WidgetTag
        className="widget"
        href={link}
        onClick={() => {
          if (typeof acceptAction !== "function") return;
          acceptAction();
        }}
      >
        <div className="side">
          {image ? (
            <div className="avatar">
              <img src={image} width="32" height="32" />
              {GameIcon && <GameIcon />}
            </div>
          ) : null}
          <h4 className="title type-form--button">
            {Array.isArray(titleOverride)
              ? t(...(titleOverride as Translation))
              : t("common:ingame", "In Game")}
          </h4>
          {text.length ? (
            <ul className="text-items">
              {text.filter(Boolean).map((textItem, i) => {
                const display = Array.isArray(textItem)
                  ? t(textItem)
                  : textItem;
                return (
                  <li key={i} className="text-item type-callout">
                    {display}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <div className="side">
          <div className="cta type-form--button">
            {Array.isArray(ctaOverride)
              ? t(...(ctaOverride as Translation))
              : t("common:returnToLiveGame", "Return to Live Game")}
          </div>
          {typeof acceptAction === "function" ? (
            <div className="accept-action type-caption">
              {t("common:enter", "Enter")}
            </div>
          ) : null}
        </div>
        {typeof countdownDuration === "number" ? (
          <TimerBar countdownDuration={countdownDuration} />
        ) : null}
      </WidgetTag>
    </header>
  );
};

const TimerBar = memo(
  ({ countdownDuration }: { countdownDuration: number }) => {
    const countdownBar = useRef(null);

    useLayoutEffect(() => {
      let time = 0;
      let timer = null;

      if (!countdownBar.current) return;
      if (typeof countdownDuration !== "number") return;

      function timeout() {
        timer = setTimeout(function () {
          if (time <= countdownDuration) {
            const offset = (time / countdownDuration) * 100;
            countdownBar.current.style.setProperty(
              "--offset",
              `${offset * -1}%`,
            );
            time++;
            timeout();
          }
        }, 1000);
      }

      timeout();

      return () => {
        clearTimeout(timer);
      };
    }, [countdownDuration, countdownBar]);

    return (
      <div
        ref={countdownBar}
        className="countdown-bar"
        style={{
          animationDuration: countdownDuration && `${countdownDuration}s`,
        }}
      />
    );
  },
);

TimerBar.displayName = "TimerBar";

export default memo(LiveGameAcceptMatchmaking);
